import { ContractVersionInsightFragment } from 'generated/resources/graphql'
import moment from 'moment'

// Types for both compensation systems
type NewAdditionalCompensation =
  ContractVersionInsightFragment['additionalCompensationsConnection']['nodes'][number]

type LegacyCompensation = ContractVersionInsightFragment['compensationsConnection']['nodes'][number]

export const CENTS = 100

export const MONTHS_PER_YEAR = 12

// Legacy compensations: fixed and up_to
const LEGACY_FIXED_COMPENSATION_TYPE = 'fixed'
const LEGACY_UP_TO_COMPENSATION_TYPE = 'up_to'
const LEGACY_ALLOWED_COMPENSATION_TYPES = [
  LEGACY_FIXED_COMPENSATION_TYPE,
  LEGACY_UP_TO_COMPENSATION_TYPE,
]

// New compensations: fixed and variable strategies
const FIXED_AMOUNT_STRATEGY_TYPE = 'contracts_fixed_amount_strategy'
const VARIABLE_AMOUNT_STRATEGY_TYPE = 'contracts_variable_amount_strategy'
const ALLOWED_NEW_COMPENSATION_TYPES = [FIXED_AMOUNT_STRATEGY_TYPE, VARIABLE_AMOUNT_STRATEGY_TYPE]

export const getXTicksFromMonth = (startMonth: string, currentDate: string) => {
  const now = moment(currentDate).endOf('month') // Current date at the start of the current month
  const end = moment(startMonth, 'YYYY-MM-DD').endOf('month') // Start date at the start of the month

  const ticks = []

  // If the start date is within the last 12 months, adjust it to show the last 12 months
  const diffMonths = now.diff(end, 'months')
  if (diffMonths < 12) {
    end.subtract(12 - diffMonths, 'months') // Go back to cover at least 12 months
  }

  // Generate ticks at a 1-month interval from the adjusted start date to the current month
  const currentTick = moment(end)
  while (currentTick.isSameOrBefore(now)) {
    ticks.push(currentTick.format('YYYY-MM-DD'))
    currentTick.add(1, 'months') // Move to the next month
  }

  return ticks
}

export const getContractVersionInMonth = (
  contractVersions: ContractVersionInsightFragment[],
  month: string
) => {
  // Convert the input month string to a moment date at the start of the month
  const targetMonth = moment(month, 'YYYY-MM-DD').endOf('month')

  // Find the latest version whose effectiveOn is on or before the target month
  const contractVersion = contractVersions
    .slice()
    .reverse()
    .find((contract) => {
      const effectiveOn = moment(contract.effectiveOn, 'YYYY-MM-DD')

      return targetMonth.isSameOrAfter(effectiveOn)
    })

  // Return the found contract version
  return contractVersion
}

const getNewCompensationAmount = (compensation: NewAdditionalCompensation): number | null => {
  const amountStrategy = compensation.amountStrategy
  if (!amountStrategy) return null

  // For fixed strategies, use the amount directly
  if (compensation.amountStrategyType === FIXED_AMOUNT_STRATEGY_TYPE) {
    if ('amount' in amountStrategy && amountStrategy.amount) {
      return Number(amountStrategy.amount)
    }
    return null
  }

  // For variable strategies, use upperLimit only (not amount)
  // This excludes "no limit" variable compensations
  if (compensation.amountStrategyType === VARIABLE_AMOUNT_STRATEGY_TYPE) {
    if ('upperLimit' in amountStrategy && amountStrategy.upperLimit) {
      return Number(amountStrategy.upperLimit)
    }
    return null
  }

  return null
}

// Calculate how many times per year a compensation is paid based on recurrence
const getPaymentsPerYear = (recurrence: string | null | undefined): number => {
  // Null/undefined recurrence represents a one-time (non-recurring) payment
  if (recurrence == null) {
    return 1
  }
  if (recurrence === 'monthly') {
    return MONTHS_PER_YEAR
  }
  const months = parseInt(recurrence.split('_')[1] ?? '1', 10)
  return MONTHS_PER_YEAR / months
}

// Calculate the annualized amount of all compensations (for annual display)
export const newCompensationsAnnualAmount = (contractVersion: ContractVersionInsightFragment) => {
  return contractVersion.additionalCompensationsConnection.nodes.reduce((acc, compensation) => {
    // Include both fixed and variable compensation types
    if (!ALLOWED_NEW_COMPENSATION_TYPES.includes(compensation.amountStrategyType ?? '')) {
      return acc
    }
    const amount = getNewCompensationAmount(compensation)
    if (amount) {
      // Multiply by number of payments per year based on recurrence
      const paymentsPerYear = getPaymentsPerYear(compensation.recurrence)
      return acc + amount * paymentsPerYear
    }
    return acc
  }, 0)
}

const getLegacyRecurrenceCount = (compensation: LegacyCompensation): number => {
  // When syncWithSupplements is true, use recurrenceCount
  if (compensation.syncWithSupplements) {
    return compensation.recurrenceCount ?? 1
  }

  if (!compensation.recurrence) {
    return 1
  }
  if (compensation.recurrence === 'monthly') {
    return 1
  }
  return parseInt(compensation.recurrence?.split('_')[1] ?? '1', 10)
}

// Calculate how many times per year a legacy compensation is paid
const getLegacyPaymentsPerYear = (compensation: LegacyCompensation): number => {
  const recurrenceCount = getLegacyRecurrenceCount(compensation)
  return MONTHS_PER_YEAR / recurrenceCount
}

// Calculate the annualized amount of all legacy compensations (for annual display)
export const legacyCompensationsAnnualAmount = (
  contractVersion: ContractVersionInsightFragment
) => {
  return contractVersion.compensationsConnection.nodes.reduce((acc, compensation) => {
    // Include fixed and up_to types only
    if (!LEGACY_ALLOWED_COMPENSATION_TYPES.includes(compensation.compensationType ?? '')) {
      return acc
    }

    const { amount, yearlyEquivalentAmount } = compensation

    // If yearlyEquivalentAmount is available, use it directly
    if (typeof yearlyEquivalentAmount === 'number' || typeof yearlyEquivalentAmount === 'string') {
      return acc + Number(yearlyEquivalentAmount)
    }

    // Otherwise, calculate annual amount based on payment amount and frequency
    if (typeof amount === 'number' || typeof amount === 'string') {
      const paymentsPerYear = getLegacyPaymentsPerYear(compensation)
      return acc + Number(amount) * paymentsPerYear
    }

    return acc
  }, 0)
}
